import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

const inputCls = "w-full rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-white";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email, origin_url: window.location.origin }); setSent(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md px-4 py-24" data-testid="forgot-password-page">
      <Logo className="mx-auto h-20" />
      <h1 className="mt-8 text-center font-display text-3xl uppercase text-chrome">Reset Password</h1>
      {sent ? <p className="mt-6 text-center text-neutral-300" data-testid="forgot-sent">If that email has admin access, a reset link is on its way. It works once and expires in 60 minutes.</p> : (
        <form onSubmit={submit} className="glass mt-8 space-y-3 rounded-3xl p-6">
          <input type="email" required placeholder="Your admin email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className={inputCls} />
          <button type="submit" disabled={busy} data-testid="forgot-submit" className="btn-gold w-full rounded-full py-3.5 font-display text-lg uppercase disabled:opacity-60">{busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Email me a reset link"}</button>
        </form>
      )}
      <Link to="/admin/login" className="mt-6 block text-center font-mono-label text-[11px] uppercase tracking-wider text-neutral-500 hover:text-white">← Back to login</Link>
    </div>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) return toast.error("Passwords don't match.");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw.a }); toast.success("Password updated. Please log in."); nav("/admin/login"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md px-4 py-24" data-testid="reset-password-page">
      <Logo className="mx-auto h-20" />
      <h1 className="mt-8 text-center font-display text-3xl uppercase text-chrome">Choose A New Password</h1>
      <form onSubmit={submit} className="glass mt-8 space-y-3 rounded-3xl p-6">
        <input type="password" required minLength={8} placeholder="New password (min 8)" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} data-testid="reset-password" className={inputCls} />
        <input type="password" required placeholder="Confirm password" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} data-testid="reset-confirm" className={inputCls} />
        <button type="submit" disabled={busy || !token} data-testid="reset-submit" className="btn-gold w-full rounded-full py-3.5 font-display text-lg uppercase disabled:opacity-60">{busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Save new password"}</button>
      </form>
    </div>
  );
}
